import { Button } from "@/components/button";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/chart";
import { LoadingSpinner } from "@/components/loading-spinner";
import { useStability } from "@/http/api-hooks";
import { Experiment, StabilityMetric } from "@/http/api-types";
import { cn } from "@/lib/utils";
import {
  buildStabilityChartData,
  hasStabilityMetricData,
  stabilityMetricLabel,
  toISODate,
} from "@/pages/experiments/stability";
import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

const METRICS: StabilityMetric[] = ["mdape", "wape"];

type StabilityTabProps = {
  experiment: Experiment;
};

export function StabilityTab({ experiment }: StabilityTabProps) {
  const { data, isLoading, error } = useStability();
  const [metric, setMetric] = useState<StabilityMetric>("mdape");
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const models = useMemo(() => data?.models ?? [], [data]);

  const window = useMemo(
    () => ({ start: toISODate(experiment.start_date) ?? "", end: toISODate(experiment.end_date) ?? "" }),
    [experiment.start_date, experiment.end_date],
  );

  // The chart end is capped at today since future cut-offs don't exist yet.
  const range = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { start: window.start, end: window.end && window.end < today ? window.end : today };
  }, [window]);

  // Series + colours are derived from the full model list so a model keeps its
  // colour as it is toggled on and off.
  const { series, rows } = useMemo(() => buildStabilityChartData(models, metric, range), [models, metric, range]);
  const hasMetricData = useMemo(() => hasStabilityMetricData(rows, series), [rows, series]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};
    for (const def of series) {
      config[def.key] = { label: def.name, color: def.color };
    }
    return config;
  }, [series]);

  const visibleSeries = series.filter(def => !hidden.has(def.name));

  const toggleModel = (name: string) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  function renderBody() {
    if (isLoading) {
      return (
        <div className="flex h-[360px] items-center justify-center">
          <LoadingSpinner />
        </div>
      );
    }

    if (error) {
      return <div className="text-sm text-red-600">Failed to load stability data.</div>;
    }

    if (models.length === 0) {
      return <div className="text-sm text-muted-foreground">No stability analysis data is available yet.</div>;
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <div className="flex shrink-0 items-center gap-1 rounded-lg border border-border/70 p-1">
            {METRICS.map(option => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={metric === option ? "default" : "ghost"}
                className="h-7 px-3 text-xs"
                onClick={() => setMetric(option)}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>

        {!hasMetricData ? (
          <div className="flex h-[420px] items-center justify-center text-sm text-muted-foreground">
            No {stabilityMetricLabel(metric)} data within the experiment window.
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[420px] w-full">
            <LineChart data={rows} margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: number) => value.toFixed(2)}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              {visibleSeries.map(def => (
                <Line
                  key={def.key}
                  type="linear"
                  dataKey={def.key}
                  name={def.name}
                  stroke={def.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ChartContainer>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Models</h4>
            <span className="text-xs text-muted-foreground">{stabilityMetricLabel(metric)}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {series.map(def => {
              const isHidden = hidden.has(def.name);
              return (
                <button
                  key={def.key}
                  type="button"
                  onClick={() => toggleModel(def.name)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border border-border/70 px-2.5 py-1 text-xs transition-colors",
                    isHidden ? "opacity-40 hover:opacity-70" : "hover:bg-muted/50",
                  )}
                  aria-pressed={!isHidden}
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: def.color }} />
                  <span className={cn(isHidden && "line-through")}>{def.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold tracking-tight">Stability</h4>
        <p className="text-xs text-muted-foreground">
          Drift of each model&apos;s global predictions per training cut-off (last_period_end) over the experiment
          window ({window.start} – {window.end}). Lower = more stable.
        </p>
      </div>
      {renderBody()}
    </div>
  );
}
